import { load } from "cheerio";
import type { CheerioAPI, Cheerio } from "cheerio";
import type { Element } from "domhandler";
import {
  createDefaultTemplateContent,
  createSectionBlock,
} from "@templatical/types";
import type { Block, TemplateContent } from "@templatical/types";
import { readLinkUnderline, resolveCssStyles } from "./css-resolver";
import {
  convertElement,
  isTableContainer,
  walkContentNodes,
} from "./block-mapper";
import { processTable } from "./section-builder";
import {
  parseColor,
  parseFontFamily,
  parsePxValue,
  parseStyleAttribute,
} from "./style-parser";
import type { ImportReport, ImportReportEntry, ImportResult } from "./types";

function emptyPadding() {
  return { top: 0, right: 0, bottom: 0, left: 0 };
}

function readPreheader($: CheerioAPI): string | undefined {
  // Convention: a hidden <div> at the very top with `display:none` containing
  // the preheader text. Match if it appears in the first ~3 children of body.
  const candidates = $("body")
    .children()
    .slice(0, 5)
    .filter((_, el) => {
      const styles = parseStyleAttribute($(el).attr("style"));
      return (styles.display ?? "").toLowerCase() === "none";
    });
  if (candidates.length === 0) return undefined;
  const text = $(candidates[0]).text().trim();
  return text || undefined;
}

/**
 * `linkUnderline` is what the source's stylesheet states for every link, as
 * `readLinkUnderline` reads it. A source stating nothing underlines its links,
 * which is the browser's default.
 */
function extractSettings(
  $: CheerioAPI,
  linkUnderline: boolean | undefined,
): TemplateContent["settings"] {
  const $body = $("body");
  const bodyStyles = parseStyleAttribute($body.attr("style"));
  const fontFamily = parseFontFamily(bodyStyles["font-family"]) || "Arial";
  const backgroundColor =
    parseColor(bodyStyles["background-color"]) ||
    parseColor(bodyStyles.background) ||
    "#ffffff";

  // Width heuristic: outermost table width attr, or ".container" width style.
  const $outerTable = $body.find("table").first();
  const widthAttr = parsePxValue($outerTable.attr("width"));
  const widthStyle = parsePxValue(
    parseStyleAttribute($outerTable.attr("style")).width,
  );
  const width = widthAttr || widthStyle || 600;

  const preheaderText = readPreheader($);

  return {
    width,
    backgroundColor,
    textColor: "#1a1a1a",
    linkUnderline: linkUnderline ?? true,
    fontFamily,
    locale: "en",
    ...(preheaderText ? { preheaderText } : {}),
  };
}

/**
 * Wrap a list of free-floating blocks (those produced by top-level non-table
 * elements) in a single one-column section, and report the section.
 *
 * The section corresponds to no source element, so the report names `body` and
 * says the section is synthetic — otherwise a caller counting sections against
 * the rows it can see in the source finds one it cannot account for. Nothing is
 * lost on this path: every loose block keeps its order inside the one column,
 * which is why the status is `converted` rather than an approximation.
 */
function wrapInSection(blocks: Block[], entries: ImportReportEntry[]): Block {
  entries.push({
    sourceTag: "body",
    templaticalBlockType: "section",
    status: "converted",
    note: "Loose top-level content was grouped into a synthetic single-column section.",
  });
  return createSectionBlock({
    columns: "1",
    children: [blocks],
    styles: {
      padding: emptyPadding(),
    },
  });
}

/**
 * Walk the body's child nodes. Tables become sections; loose content is
 * accumulated and wrapped in a single one-column section.
 *
 * Both walks below go through `walkContentNodes`, the same node
 * classification the cell walk uses, so bare text and inline markup at body
 * level and inside a layout container reach a rich-text block. A walk over
 * `children()` visits neither, which drops copy the source email displays —
 * `Lead<h2>H</h2>Trailing` imported as the heading alone.
 *
 * `bodyWidth` is the room a line has across a top-level section, which is
 * what a divider's px width is measured against.
 */
function processBody(
  $: CheerioAPI,
  entries: ImportReportEntry[],
  warnings: string[],
  bodyWidth: number,
): Block[] {
  const blocks: Block[] = [];
  const $body = $("body");

  let pendingLoose: Block[] = [];

  const flushLoose = () => {
    if (pendingLoose.length > 0) {
      blocks.push(wrapInSection(pendingLoose, entries));
      pendingLoose = [];
    }
  };

  const collectLoose = ({
    block,
    entry,
  }: {
    block: Block;
    entry: ImportReportEntry;
  }) => {
    entries.push(entry);
    pendingLoose.push(block);
  };

  /**
   * Descend a layout container, taking its tables as sections and everything
   * else as loose content. A container holding another container descends
   * again, so a table reaches `processTable` at whatever depth the wrapper
   * markup buries it — MJML nests an outer body div around one div per
   * section around the section's table, and a single-level walk sees only the
   * middle div, which the block mapper turns into one paragraph swallowing
   * the entire table subtree.
   *
   * Bounded by DOM depth: a container is descended only when it holds a
   * table, and each step moves to a child.
   *
   * Declared here so every depth shares `pendingLoose` and `flushLoose`. A
   * per-level accumulator flushes at the wrong point and reorders the
   * document: content sitting before a nested table lands after it.
   */
  const walkContainer = ($container: Cheerio<Element>): void => {
    walkContentNodes($container, $, collectLoose, ($inner, innerTag) => {
      if (innerTag === "table") {
        // Flush loose content accumulated BEFORE this table so it keeps its
        // source position, mirroring the top-level walk. Without this, the
        // table is appended immediately while leading siblings are flushed
        // only after the walk — reordering the document.
        flushLoose();
        blocks.push(
          ...processTable($inner, $, entries, warnings, false, "", bodyWidth),
        );
        return;
      }

      if (isTableContainer($inner, innerTag)) {
        walkContainer($inner);
        return;
      }

      const r = convertElement($inner, $, bodyWidth);
      if (r) {
        entries.push(r.entry);
        pendingLoose.push(r.block);
      }
    });
  };

  walkContentNodes($body, $, collectLoose, ($child, tag) => {
    if (tag === "table") {
      flushLoose();
      blocks.push(
        ...processTable($child, $, entries, warnings, false, "", bodyWidth),
      );
      return;
    }

    // Skip hidden preheader divs — already captured in settings.
    const childStyles = parseStyleAttribute($child.attr("style"));
    if ((childStyles.display ?? "").toLowerCase() === "none") return;

    // Containers like a wrapping <div> with table children: descend.
    if (isTableContainer($child, tag)) {
      flushLoose();
      walkContainer($child);
      flushLoose();
      return;
    }

    const r = convertElement($child, $, bodyWidth);
    if (r) {
      entries.push(r.entry);
      pendingLoose.push(r.block);
    }
  });

  flushLoose();
  return blocks;
}

/**
 * Converts an HTML email template to Templatical TemplateContent.
 *
 * Designed for table-based marketing email HTML (output of MJML, Mailchimp,
 * SendGrid, Campaign Monitor, hand-coded emails). Modern HTML using flex/grid
 * layouts is preserved via HTML-fallback blocks.
 *
 * @param html - The raw HTML string (full document or body fragment).
 * @returns An ImportResult with the converted content and a detailed report.
 *
 * @example
 * ```ts
 * import { convertHtmlTemplate } from '@templatical/import-html';
 *
 * const html = await fetch('/email.html').then((r) => r.text());
 * const { content, report } = convertHtmlTemplate(html);
 *
 * const editor = init({ container: '#editor', content });
 *
 * console.log(report.summary);
 * console.log(report.warnings);
 * ```
 */
export function convertHtmlTemplate(html: string): ImportResult {
  if (typeof html !== "string") {
    throw new Error(
      "Invalid HTML template: expected a string. Pass the raw HTML source as a string.",
    );
  }
  if (html.trim().length === 0) {
    throw new Error(
      "Invalid HTML template: input is empty. Pass the raw HTML source of an email.",
    );
  }

  const $ = load(html);
  const linkUnderline = readLinkUnderline($);
  resolveCssStyles($);

  // Drop tags that are never useful in the editor canvas.
  $("script, noscript, link, meta, title").remove();

  const entries: ImportReportEntry[] = [];
  const warnings: string[] = [];

  const settings = extractSettings($, linkUnderline);
  const blocks = processBody($, entries, warnings, settings.width);

  if (blocks.length === 0) {
    warnings.push(
      "No convertible content was found in the HTML. The email may use a non-table layout — modern HTML support is limited.",
    );
  }

  const content: TemplateContent = {
    ...createDefaultTemplateContent(),
    blocks,
    settings,
  };

  const summary = {
    total: entries.length,
    converted: entries.filter((e) => e.status === "converted").length,
    approximated: entries.filter((e) => e.status === "approximated").length,
    htmlFallback: entries.filter((e) => e.status === "html-fallback").length,
    skipped: entries.filter((e) => e.status === "skipped").length,
  };

  const report: ImportReport = { entries, warnings, summary };

  return { content, report };
}
